/**
 * Pick the approved template a WhatsApp campaign goes out as.
 *
 * This is not a nicety. A business cannot send free text to somebody who has
 * not messaged it in the last 24 hours, so a campaign — which is always
 * business-initiated — is the template. Its body is fixed text that Meta has
 * already approved, and only the {{1}}, {{2}} … placeholders in it take
 * values, which is why the count matters and is shown.
 */
import { useEffect, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { isOffline } from '../../lib/storage.js';
import api from '../../lib/api.js';

export default function WhatsAppTemplates() {
  const { S, setChannelSetting } = useApp();
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  /* Whether the server can list templates at all. Through Chatlio it cannot
     - the templates live in Chatlio's panel - so the name is typed in. */
  const [canList, setCanList] = useState(true);

  const M = S.mkt || {};
  const chosen = M.waTemplate || '';

  useEffect(() => {
    if (isOffline()) return;
    api.whatsapp.templates()
      .then(out => {
        setList(out.templates || []);
        setCanList(out.configured !== false);
      })
      .catch(e => setError(e.message));
  }, []);

  const pick = name => {
    const t = (list || []).find(x => x.name === name);
    setChannelSetting('waTemplate', name);
    // The language and placeholder count travel with the choice: Meta refuses
    // a template sent in the wrong language or with the wrong parameter count.
    setChannelSetting('waTemplateLang', t ? t.language : '');
    setChannelSetting('waTemplateVars', t ? t.vars : 0);
  };

  if (isOffline()) return <p className="muted">Sign in to load your approved templates.</p>;
  if (error) return <p className="muted">Could not load templates — {error}</p>;
  if (!list) return <p className="muted">Loading approved templates…</p>;

  /*
   * Through Chatlio there is no list to offer: the templates are approved on
   * Meta and shown in Chatlio's own panel, and its API does not hand them
   * back. So the name is typed exactly as Chatlio shows it, with the language
   * and the placeholder count beside it - Meta refuses a template sent in the
   * wrong language or with the wrong number of values, so both matter.
   */
  if (!canList) {
    return (
      <div className="form">
        <div className="f">
          <label>Template name</label>
          <input
            value={chosen}
            placeholder="exactly as it appears in Chatlio → Templates"
            onChange={e => setChannelSetting('waTemplate', e.target.value.trim())}
          />
        </div>
        <div className="f">
          <label>Language</label>
          <input
            value={M.waTemplateLang || ''}
            placeholder="en, en_US, ar…"
            onChange={e => setChannelSetting('waTemplateLang', e.target.value.trim())}
          />
        </div>
        <div className="f">
          <label>Placeholders in the body</label>
          <input
            type="number"
            min="0"
            max="20"
            value={M.waTemplateVars || 0}
            onChange={e => setChannelSetting('waTemplateVars', Math.max(0, Number(e.target.value) || 0))}
          />
          <div className="fhint">
            How many {'{{1}}'}, {'{{2}}'}… the approved body has. Open the template in
            Chatlio and count them.
          </div>
        </div>
        <div className="f wide">
          <div className="fhint">
            This is the default for new WhatsApp campaigns; each campaign can name a different
            one. {chosen
              ? `Campaigns will go out as “${chosen}” in ${M.waTemplateLang || 'en'}${M.waTemplateVars ? `, filling ${M.waTemplateVars} placeholder${M.waTemplateVars > 1 ? 's' : ''}` : ''}.`
              : 'Nothing chosen yet.'}
          </div>
        </div>
      </div>
    );
  }

  if (!list.length) {
    return (
      <p className="muted">
        No approved templates on this account yet. A campaign can only reach someone who has
        not messaged you in the last 24 hours through an approved template.
      </p>
    );
  }

  const current = list.find(t => t.name === chosen);

  return (
    <>
      <div className="num">
        <label>Approved template</label>
        <select value={chosen} onChange={e => pick(e.target.value)}>
          <option value="">— none, free text within the 24-hour window —</option>
          {list.map(t => (
            <option key={`${t.name}:${t.language}`} value={t.name}>
              {t.name} · {t.language}{t.vars ? ` · ${t.vars} field${t.vars > 1 ? 's' : ''}` : ''}
            </option>
          ))}
        </select>
        <small>{list.length} approved on this account</small>
      </div>

      {current ? (
        <div className="cpreview" style={{ marginTop: 11 }}>
          <div className="cpbody">
            {current.body || current.preview || 'This template has no text body.'}
            {current.vars
              ? `\n\n${current.vars} placeholder${current.vars > 1 ? 's' : ''} will be filled from the campaign.`
              : '\n\nFixed text — the campaign message is not used for WhatsApp.'}
          </div>
        </div>
      ) : null}
    </>
  );
}
